import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CARDIOVASCULAR_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for LDL Cholesterol Management Study
 */
export const ldlScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "statin_therapy",
    type: QuestionType.RADIO,
    question: "Are you taking medication to manage high cholesterol?",
    helpText: "This includes statins (like atorvastatin, simvastatin, rosuvastatin) or other cholesterol-lowering medications like ezetimibe or PCSK9 inhibitors",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_statin",
        label: "Yes, I am taking cholesterol-lowering medications",
      },
      {
        value: "intolerant",
        label: "No, but I have tried them and cannot tolerate them due to side effects",
        disqualify: true,
      },
      {
        value: "never_tried",
        label: "No, I have never taken cholesterol-lowering medications",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study requires participants who are currently taking cholesterol medications.",
  },

  {
    id: "cholesterol_medicine_type",
    type: QuestionType.RADIO,
    question: "Which best describes your current cholesterol medicine?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "high_dose_statin",
        label: "A high-dose statin (this includes atorvastatin/Lipitor 40–80 mg and rosuvastatin/Crestor 20–40 mg)",
      },
      {
        value: "low_dose_intolerant",
        label: "A lower-dose statin because I had side effects from a higher dose",
        disqualify: true,
      },
      {
        value: "low_dose_other_reason",
        label: "A lower-dose statin for another reason (such as not needing a higher dose)",
        disqualify: true,
      },
      {
        value: "different_med_intolerant",
        label: "A different cholesterol medicine and I previously tried a statin but had to stop due to side effects",
        disqualify: true,
      },
      {
        value: "different_med_never_tried",
        label: "A different cholesterol medicine and I have never tried a statin",
        disqualify: true,
      },
      {
        value: "unsure",
        label: "Unsure",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study requires participants who are currently taking a high-dose statin.",
    attributeMapping: mappers.value({
      high_dose_statin: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.ON_HIGH_DOSE_STATIN, booleanAttributeValue: true },
      ],
      low_dose_intolerant: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.ON_HIGH_DOSE_STATIN, booleanAttributeValue: false },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STATIN_INTOLERANCE, booleanAttributeValue: true },
      ],
      low_dose_other_reason: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.ON_HIGH_DOSE_STATIN, booleanAttributeValue: false },
      ],
      different_med_intolerant: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.ON_HIGH_DOSE_STATIN, booleanAttributeValue: false },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STATIN_INTOLERANCE, booleanAttributeValue: true },
      ],
      different_med_never_tried: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.ON_HIGH_DOSE_STATIN, booleanAttributeValue: false },
      ],
      unsure: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.ON_HIGH_DOSE_STATIN, booleanAttributeValue: false },
      ],
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT,
    }),
  },

  {
    ...standardWeightQuestion,
    helpText: "We'll calculate your BMI (Body Mass Index) using your height and weight.",
    attributeMapping: mappers.weight({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT,
    }),
  },

  {
    id: "cardiovascular_disease_history",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following cardiovascular conditions?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "heart_attack_disease",
        label: "Heart attack or heart disease",
      },
      {
        value: "stroke_tia",
        label: "Stroke or \"mini-stroke\" (TIA)",
      },
      {
        value: "peripheral_artery_disease",
        label: "Narrowing or blockages in the arteries of your legs or neck (often called \"peripheral artery disease\" or \"carotid artery stenosis\")",
      },
      {
        value: "none",
        label: "None of the above",
      },
    ],
    attributeMapping: mappers.multiSelect({
      valueMap: {
        heart_attack_disease: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        ],
        stroke_tia: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
        ],
        peripheral_artery_disease: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "cardiovascular_risk_factors",
    type: QuestionType.CHECKBOX,
    question: "Do you have, or have you ever been told by a doctor that you have any of the following risk factors?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "high_blood_pressure",
        label: "High blood pressure",
      },
      {
        value: "diabetes",
        label: "Type 1 or Type 2 diabetes",
      },
      {
        value: "chronic_kidney_disease",
        label: "Chronic kidney disease",
      },
      {
        value: "family_history_heart_disease",
        label: "Family history of early heart disease (for example: a parent, brother, or sister who had a heart attack or stroke before age 55 for men or 65 for women)",
      },
      {
        value: "smoking",
        label: "Smoking cigarettes, vapes, or other tobacco products (currently or in the past)",
      },
      {
        value: "overweight_obesity",
        label: "Overweight or obesity",
      },
      {
        value: "calcified_coronary_arteries",
        label: "Narrowed or calcified coronary arteries found on imaging tests such as a CT scan",
      },
      {
        value: "none",
        label: "None of the above",
        disqualify: (allAnswers, currentAnswers) => {
          const bmi = allAnswers?.["bmi"];
          const noneSelected = currentAnswers?.includes("none");
          const cvDiseaseHistory = allAnswers?.["cardiovascular_disease_history"] as string[] | undefined;
          const hasCardiovascularDisease = cvDiseaseHistory && cvDiseaseHistory.length > 0 && !cvDiseaseHistory.includes("none");
          
          // Only disqualify if:
          // 1. They selected "none" for risk factors AND
          // 2. They have no cardiovascular disease history AND  
          // 3. Their BMI is less than 30
          if (noneSelected && !hasCardiovascularDisease && typeof bmi === "number" && bmi < 30) {
            return true;
          }
          return false;
        },
      },
    ],
    disqualifyMessage: "This study requires participants with cardiovascular disease, cardiovascular risk factors, or BMI ≥30.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        high_blood_pressure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: true },
        ],
        diabetes: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
        ],
        chronic_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
        family_history_heart_disease: [
          { attributeDefinitionId: 25452, booleanAttributeValue: true },
        ],
        smoking: [
          { attributeDefinitionId: 20917, booleanAttributeValue: true },
        ],
        overweight_obesity: [], // BMI ≥30 used for automatic qualification logic
        calcified_coronary_arteries: [
          { attributeDefinitionId: 3436, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: false },
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: 25452, booleanAttributeValue: false },
          { attributeDefinitionId: 20917, booleanAttributeValue: false },
          { attributeDefinitionId: 3436, booleanAttributeValue: false },
        ],
      },
    }),
  },









  {
    id: "major_medical_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "cancer_recent",
        label: "I have had a cancer diagnosis or treatment within the past 5 years (excluding SCC, BCC, and in-situ cervical cancer)",
        disqualify: true,
      },
      {
        value: "organ_transplant",
        label: "I have had a major organ transplant",
        disqualify: true,
      },

      {
        value: "uncontrolled_diabetes",
        label: "I have uncontrolled diabetes (HbA1c ≥ 9.5%)",
        disqualify: true,
      },
      {
        value: "severe_heart_failure",
        label: "I have severe heart failure",
        disqualify: true,
      },
      {
        value: "end_stage_kidney_disease",
        label: "I have end stage kidney failure or I am on dialysis",
        disqualify: true,
      },
      {
        value: "liver_failure_cirrhosis",
        label: "I have liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "hepatitis_b_c",
        label: "I have hepatitis B or hepatitis C",
        disqualify: true,
      },
      {
        value: "pregnancy_breastfeeding",
        label: "I am currently pregnant, breastfeeding, or planning to become pregnant",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        cancer_recent: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
        ],
        organ_transplant: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true },
        ],
        uncontrolled_diabetes: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: true },
        ],
        severe_heart_failure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
        ],
        pregnancy_breastfeeding: [
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
        ],
        end_stage_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
        liver_failure_cirrhosis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
        ],
        hepatitis_b_c: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false },
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
        ],
      },
    }),
  },



  {
    id: "recent_medications",
    type: QuestionType.CHECKBOX,
    question: "Have you recently taken any of the following medications?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "inclisiran",
        label: "I have taken inclisiran (Leqvio) within the past 18 months",
        disqualify: true,
      },
      {
        value: "other_pcsk9_inhibitors",
        label: "I have taken other PCSK9 inhibitors (Repatha, Praluent) within the past 3 months",
        disqualify: true,
      },
      {
        value: "mipomersen_lomitapide",
        label: "I have taken mipomersen (Kynamro) or lomitapide (Juxtapid) within the past 12 months",
        disqualify: true,
      },
      {
        value: "gemfibrozil",
        label: "I have taken gemfibrozil (Lopid) within the past week or I am currently taking it",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "This study requires a washout period from certain cholesterol medications.",
  },



  {
    id: "study_compliance",
    type: QuestionType.RADIO,
    question: "Are you willing and able to comply with study requirements?",
    helpText: "This includes regular clinic visits, blood tests, and taking study medication for the duration of the study (approximately 2 years).",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am able to comply with the study requirements",
      },
      {
        value: "no",
        label: "No, I am not able to comply with the study requirements",
        disqualify: true,
      },
      {
        value: "discuss",
        label: "I would like to discuss the requirements further with the research team",
      },
    ],
    disqualifyMessage: "This study requires participants who are willing and able to comply with all study requirements.",
  },

  {
    id: "currentTrial",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not enrolling in or participating in any other clinical trials",
      },
      {
        value: "yes",
        label: "Yes, I am currently enrolling in or participating in another clinical trial",
        disqualify: true,
      },
    ],
    disqualifyMessage: "You can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },
]; 